import React, { useState, useEffect, useCallback, useRef } from "react";
import ProteinTracker from "./ProteinTracker";
import DayNavigator from "../../components/DayNavigator";
import TodayProgress from "./TodayProgress";
import WeeklyLog from "./WeeklyLog";
import { syncLogToShopify } from "../../utils/syncLogToShopify";
import { launchConfetti } from "../../utils/confetti";

const meals = ["Breakfast", "Snack 1", "Lunch", "Snack 2", "Dinner"];

const formatDate = (date) => {
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const getDateStrFromDate = (date) => {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return local.toISOString().split("T")[0];
};

const DailyMealLogger = ({ onLog }) => {
  const saveTimeout = useRef(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayNumber, setDayNumber] = useState(1);
  const [openMeal, setOpenMeal] = useState(null);
  const [mealTotals, setMealTotals] = useState({});
  const [totalProteinForDay, setTotalProteinForDay] = useState(0);
  const [log, setLog] = useState(() => {
    return JSON.parse(localStorage.getItem("custom.hyman_hive_log")) || {};
  });

  // Add local state for saving log
  const [savingLog, setSavingLog] = useState(false);

  // Store startDate in state and set it only once or if fallback logic is needed
  const [startDate, setStartDate] = useState(() =>
    localStorage.getItem("custom.hyman_hive_start_date")
  );

  // Fallback logic for startDate: if not set, use the selectedDate as the start date
  useEffect(() => {
    if (!startDate) {
      const firstLoggedDay = Object.values(log)[0];
      const firstDate = firstLoggedDay?.date;
      if (firstDate) {
        const dateStr = getDateStrFromDate(selectedDate);

        localStorage.setItem("custom.hyman_hive_start_date", dateStr);
        setStartDate(dateStr);
        syncLogToShopify(
          "single_line_text_field",
          dateStr,
          "hyman_hive_start_date"
        );
      }
    }
  }, [log, selectedDate, startDate]);

  const getDayNumberFromDate = (selectedDate) => {
    if (!startDate) return 1;
    const msPerDay = 1000 * 60 * 60 * 24;
    return (
      Math.floor(
        (new Date(getDateStrFromDate(selectedDate)) - new Date(startDate)) /
          msPerDay
      ) + 1
    );
  };

  useEffect(() => {
    if (selectedDate && startDate) {
      const newDayNumber = getDayNumberFromDate(selectedDate);
      setDayNumber(newDayNumber);
    }
  }, [selectedDate, startDate]);

  useEffect(() => {
    const dayKey = `Day ${dayNumber}`;
    const entries = log[dayKey]?.entries || {};
    const newTotals = {};
    let totalForDay = 0;

    for (const meal of meals) {
      const mealEntries = entries[meal]?.protein || [];
      const mealTotal = mealEntries.reduce((sum, e) => sum + e.grams, 0);
      newTotals[meal] = mealTotal;
      totalForDay += mealTotal;
    }

    setMealTotals(newTotals);
    setTotalProteinForDay(totalForDay);
  }, [dayNumber, log, selectedDate]);

  const parseLocalDateString = (str) => {
    if (!str) return new Date();
    const [year, month, day] = str.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const updateMeal = useCallback(
    (mealName, updatedEntries) => {
      // Derive dayKey and dateStr from selectedDate and startDate
      const msPerDay = 1000 * 60 * 60 * 24;
      const baseStart = parseLocalDateString(startDate);
      const selected = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      const derivedDayNumber = Math.max(
        1,
        Math.floor((selected - baseStart) / msPerDay) + 1
      );
      const dayKey = `Day ${derivedDayNumber}`;
      const newLog = { ...log };
      const dateStr = selected.toISOString().split("T")[0];

      if (!newLog[dayKey]) {
        newLog[dayKey] = {
          date: dateStr,
          entries: {},
        };
      }

      if (!newLog[dayKey].entries) {
        newLog[dayKey].entries = {};
      }

      newLog[dayKey].entries[mealName] = {
        ...(newLog[dayKey].entries[mealName] || { notes: "" }),
        protein: updatedEntries,
      };

      setLog(newLog);
      localStorage.setItem("custom.hyman_hive_log", JSON.stringify(newLog));
    },
    [selectedDate, startDate, log]
  );

  // Handler for updating meal notes
  const updateMealNote = (mealName, note) => {
    console.log(note);
    const dayKey = `Day ${dayNumber}`;
    const newLog = { ...log };
    if (!newLog[dayKey]) return;
    if (!newLog[dayKey].entries[mealName]) return;
    newLog[dayKey].entries[mealName].notes = note;
    setLog(newLog);
    localStorage.setItem("custom.hyman_hive_log", JSON.stringify(newLog));
  };

  const dayKey = `Day ${dayNumber}`;
  const dayLog = log[dayKey]?.entries || {};

  return (
    <div className="tw-flex tw-gap-6 tw-flex-start tw-justify-start tw-flex-col-reverse lg:tw-flex-row">
      <div className="tw-flex tw-mb-6 tw-w-full lg:tw-w-1/3 tw-flex-col tw-gap-6">
        <TodayProgress
          displayDate={formatDate(selectedDate)}
          totalProtein={totalProteinForDay}
          proteinPercentage={Math.min((totalProteinForDay / 90) * 100, 100)}
        />

        <WeeklyLog
          currentDayNumber={dayNumber}
          startDate={startDate}
          logData={log}
        />
      </div>

      <div className="tw-w-full daily-log lg:tw-2/3">
        <div className="tw-bg-white  dark:tw-bg-black tw-px-3 sm:tw-px-10  tw-py-4 lg:tw-py-8 tw-rounded-2xl tw-shadow-sm tw-w-full">
          <div className="welcome tw-border-b tw-pb-7 tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-justify-between dark:tw-border-gray-700">
            <DayNavigator
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              dayNumber={dayNumber}
              startDate={startDate}
            />
          </div>

          {meals.map((meal, index) => (
            <div
              key={meal}
              className={
                index === meals.length - 1
                  ? ""
                  : "tw-border-b hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 dark:tw-border-gray-700"
              }
            >
              <button
                className="tw-w-full tw-text-left tw-py-6 tw-flex tw-justify-between tw-px-3 lg:tw-px-4 tw-flex tw-items-center"
                onClick={() => setOpenMeal(openMeal === index ? null : index)}
              >
                <h3 className="tw-flex-1">{meal}</h3>

                <span className="tw-flex tw-flex-2 tw-items-center tw-gap-2">
                  <span
                    className={`tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded-full ${(() => {
                      const total =
                        mealTotals[meal] != null
                          ? mealTotals[meal].toFixed(1)
                          : "0";
                      if (total >= 30)
                        return "tw-bg-green-500 tw-text-white dark:tw-text-black";
                      if (total > 0)
                        return "tw-bg-yellow-400 tw-text-white dark:tw-text-black";
                      return "tw-bg-gray-300 tw-text-white dark:tw-text-black";
                    })()}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="tw-h-4 tw-w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  <span className="tw-min-w-[100px] tw-text-left">
                    {(mealTotals[meal] ?? 0).toFixed(0)}g protein
                  </span>
                  <svg
                    className={`tw-h-6 tw-w-6 tw-transition-transform tw-text-gray-500 ${
                      openMeal === index ? "tw-rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </span>
              </button>

              <ProteinTracker
                entries={dayLog[meal]?.protein || []}
                onChange={(updatedEntries) => updateMeal(meal, updatedEntries)}
                isVisible={openMeal === index}
                note={dayLog[meal]?.notes || ""}
                onNoteChange={(val) => updateMealNote(meal, val)}
              />
              {openMeal === index && (
                <div className="tw-relative tw-flex tw-justify-start tw-mb-8 tw-mx-6">
                  <button
                    id={`log-save-button-${index}`}
                    onClick={() => {
                      setSavingLog(true);

                      // Trigger GA4 custom event
                      gtag("event", "protein_log_saved", {
                        meal_type: meal.toLowerCase().replace(" ", "_"),
                        grams_logged: mealTotals[meal] ?? 0,
                        note_length: (dayLog[meal]?.notes || "").length,
                      });

                      const canvas = document.querySelector(
                        `#log-save-canvas-${index}`
                      );
                      const button = document.querySelector(
                        `#log-save-button-${index}`
                      );
                      if (canvas && button) launchConfetti(canvas, button);
                      syncLogToShopify("json", log, "hyman_hive_log").then(
                        () => {
                          const storedCustomProteins = localStorage.getItem(
                            "custom.custom_proteins"
                          );
                          if (storedCustomProteins) {
                            syncLogToShopify(
                              "json",
                              storedCustomProteins,
                              "custom_proteins"
                            );
                          }
                          setTimeout(() => {
                            setSavingLog(false);
                            setOpenMeal(
                              index + 1 < meals.length ? index + 1 : null
                            );
                          }, 2000);
                        }
                      );
                    }}
                    className={`tw-px-6 tw-py-3 tw-rounded-full tw-shadow-sm ${
                      savingLog
                        ? "tw-bg-gray-500 tw-text-white"
                        : "tw-bg-black hover:tw-bg-gray-700 tw-text-white"
                    }`}
                  >
                    {savingLog ? (
                      <span className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-loader tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-w-4 tw-h-4 tw-animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      `Save ${meal.toLowerCase().replace(/ 1| 2/, "")} log`
                    )}
                  </button>
                  <canvas
                    id={`log-save-canvas-${index}`}
                    style={{ transform: "translate(-25%, -25%)" }}
                    className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-pointer-events-none tw-z-[9999]"
                  ></canvas>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DailyMealLogger;
